import {
    ChatCompletions,
    ChatMessage,
    DisplaySection,
    DisplayDocument,
    DisplayField,
    PendingDisplayDocumentChanges,
    ScoutIntent,
} from "@/common/utils/types";
import { diffString } from "./word-diff";
import { apiFetch, getEffectiveSession } from "@/common/lib/api";
import { getStoredSelectedClinicAccountId } from "@/common/lib/selectedClinicAccountStorage";
import { VITE_HAPPYDOC_API_SERVER_URL } from "@/common/utils/constants";

type ScoutOperation = {
    scoutSummary: string;
    intent: ScoutIntent;
    updatedSections: DisplaySection[];
};

type LLMSuccessResponse = { success: true; message: string } & (
    | {
          intent: "edit-document";
          updatedSections: DisplaySection[];
          pendingDocumentChanges: PendingDisplayDocumentChanges;
      }
    | {
          intent: Exclude<ScoutIntent, "edit-document">;
      }
);
type LLMFailureResponse = { success: false; message: string };
export type LLMResponse = LLMSuccessResponse | LLMFailureResponse;

export interface ScoutConversationHistoryEntry {
    isUser: boolean;
    text: string;
}

/** Keep the LLM prompt bounded; must match API-Server's MAX_SCOUT_CONVERSATION_HISTORY_MESSAGES cap. */
export const MAX_SCOUT_CONVERSATION_HISTORY_MESSAGES = 10;

/**
 * Trims persisted chat history down to the most recent turns and strips it to just
 * the fields Scout needs, so prior turns can be replayed into the LLM prompt.
 */
export const buildScoutConversationHistory = (messages: ChatMessage[]): ScoutConversationHistoryEntry[] =>
    messages.slice(-MAX_SCOUT_CONVERSATION_HISTORY_MESSAGES).map(({ isUser, text }) => ({ isUser, text }));

interface DocumentContext {
    document?: ChatCompletions | null;
    documentContent: string | DisplaySection[] | null;
    documentTypeDefinition?: any;
    userInput: string;
    conversationHistory?: ScoutConversationHistoryEntry[];
    onProgress?: (message: string) => void;
}

/**
 * Process user request to Scout
 * @param userInput - The user's request
 * @param activeDocument - The active document object
 * @param conversationHistory - Recent prior turns in this conversation (optional)
 * @param onProgress - Called with a status message as Scout works (documentless chat only; optional)
 * @returns Promise<LLMResponse>
 */
export const processScoutRequest = async (
    userInput: string,
    activeDocument?: ChatCompletions | null,
    conversationHistory?: ScoutConversationHistoryEntry[],
    onProgress?: (message: string) => void,
): Promise<LLMResponse> => {
    try {
        // Prepare context for LLM
        const documentContext: DocumentContext = {
            document: activeDocument,
            documentContent: activeDocument?.chat_json?.chat_text ?? null,
            documentTypeDefinition: activeDocument?.document_snapshot,
            userInput,
            conversationHistory,
            onProgress,
        };
        const documentName = activeDocument?.chat_json?.header;
        const scoutOperation = await determineScoutOperation(documentContext);

        let response: LLMResponse;
        if (scoutOperation.intent === "edit-document") {
            if (activeDocument) {
                const pendingDocumentChanges = buildPendingDocumentChanges(
                    activeDocument,
                    scoutOperation.updatedSections,
                );
                response = {
                    success: true,
                    message:
                        scoutOperation.scoutSummary ??
                        `I've successfully processed your request${documentName ? ` for "${documentName}"` : ""}. The document has been updated with the new information.`,
                    intent: scoutOperation.intent,
                    updatedSections: scoutOperation.updatedSections,
                    pendingDocumentChanges: pendingDocumentChanges,
                };
            } else {
                response = {
                    success: false,
                    message:
                        scoutOperation.scoutSummary ??
                        `I'm sorry, I couldn't process your request because no document is open. Please open a document first.`,
                };
            }
        } else {
            response = {
                success: true,
                message: scoutOperation.scoutSummary ?? `I'm sorry, I couldn't process your request. Please try again.`,
                intent: scoutOperation.intent,
            };
        }

        return response;
    } catch (error) {
        console.error("Error processing document update:", error);
        return {
            success: false,
            message: "Sorry, I encountered an error processing your request. Please try again.",
        };
    }
};

// Helper function to normalize value to array
const normalizeToArray = (value: string | string[] | undefined): (string | undefined)[] => {
    if (value === undefined || value === null || value === "") {
        return [undefined];
    }
    if (Array.isArray(value)) {
        return value;
    }
    return [value];
};

// Helper function to strip HTML tags for comparison
const stripHtmlTags = (str: string): string => {
    return str.replace(/(<b>|<\/b>|<i>|<\/i>|<u>|<\/u>|<s>|<\/s>)/g, "");
};

/**
 * Compares two section/field values element by element and returns diffed result
 * @param oldValue - The old value (string or string[])
 * @param newValue - The new value (string or string[])
 * @returns The diffed value in the same format as the new value (string if original was string and result is single element, array otherwise)
 */
export const diffValues = (
    oldValue: string | string[] | undefined,
    newValue: string | string[] | undefined,
): string | string[] => {
    const oldValueIsArray = Array.isArray(oldValue);
    const newValueIsArray = Array.isArray(newValue);
    const differentFormats = oldValueIsArray !== newValueIsArray;
    // Normalize both values to arrays
    const oldValueArray = normalizeToArray(oldValue);
    const newValueArray = normalizeToArray(newValue);

    // Compare values one by one
    const diffedValues: string[] = [];
    // Since we're only showing the new inserted values (hiding the removed old values), only go up to the length of the new values
    for (let i = 0; i < newValueArray.length; i++) {
        const oldVal = oldValueArray[i];
        const newVal = newValueArray[i];

        if ((differentFormats || oldVal === undefined) && newVal !== undefined) {
            // New value added
            diffedValues.push(`<ins>${newVal}</ins>`);
        } else if ((differentFormats || newVal === undefined) && oldVal !== undefined) {
            // Value deleted
            diffedValues.push(`<del>${oldVal}</del>`);
        } else if (oldVal !== undefined && newVal !== undefined) {
            // Both exist, compare them
            const oldStripped = stripHtmlTags(oldVal);
            const newStripped = stripHtmlTags(newVal);

            if (oldStripped !== newStripped) {
                // Values differ, use diffString to create HTML diff
                const diffs = diffString(oldStripped, newStripped);
                diffedValues.push(diffs);
            } else {
                // Values are the same, keep the new value
                diffedValues.push(newVal);
            }
        }
        // If both are undefined (shouldn't happen), skip
    }

    // Return in the same format as the new original - if original was a string and result is single element, convert back
    if (!newValueIsArray) {
        if (diffedValues.length === 0) {
            return "";
        }
        if (diffedValues.length === 1) {
            return diffedValues[0];
        }
    }
    return diffedValues;
};

/**
 * Builds a PendingDisplayDocumentChanges from the active document and updated sections
 * @param activeDocument - The current active document
 * @param updatedSections - The updated sections from LLM response
 * @returns PendingDisplayDocumentChanges with merged content and diff HTML
 */
const buildPendingDocumentChanges = (
    activeDocument: ChatCompletions,
    updatedSections: DisplaySection[],
): PendingDisplayDocumentChanges => {
    // Get current document content
    const currentContent = activeDocument.chat_json?.chat_text;

    // If current content is a string (legacy format), handle it
    if (typeof currentContent === "string") {
        const pendingDoc: PendingDisplayDocumentChanges = {
            content: updatedSections,
            changes: updatedSections,
            accepted: false,
        };

        return pendingDoc;
    }

    // Current content is DisplayDocument (array of DisplaySection)
    const currentSections = (currentContent as DisplayDocument) || [];

    // Create a map of current sections by ID for quick lookup
    const currentSectionsMap = new Map<string, DisplaySection>();
    currentSections.forEach((section) => {
        currentSectionsMap.set(section.id, section);
    });

    // Create a map of current fields by ID (nested in sections)
    const currentFieldsMap = new Map<string, { sectionId: string; field: DisplayField }>();
    currentSections.forEach((section) => {
        if (section.fields) {
            section.fields.forEach((field) => {
                currentFieldsMap.set(field.id, { sectionId: section.id, field });
            });
        }
    });

    const updatedSectionsMap = new Map<string, DisplaySection>();
    updatedSections.forEach((section) => {
        updatedSectionsMap.set(section.id, section);
    });

    const currentSectionIds = new Set(currentSections.map((section) => section.id));
    const mergedSections = [
        ...currentSections.map((section) => updatedSectionsMap.get(section.id) ?? section),
        ...updatedSections.filter((section) => !currentSectionIds.has(section.id)),
    ];

    // Build changes array from the merged document so Scout omissions preserve current sections.
    const changes: DisplaySection[] = mergedSections.map((updatedSection) => {
        const currentSection = currentSectionsMap.get(updatedSection.id);

        // Create a copy of the updated section with all properties preserved
        const changedSection: DisplaySection = {
            ...updatedSection,
        };

        changedSection.value = diffValues(currentSection?.value, updatedSection.value);

        // Show the name as inserted if it's new or different
        if (!currentSection || currentSection.name !== updatedSection.name) {
            changedSection.name = `<ins>${updatedSection.name}</ins>`;
        }

        // Handle fields if they exist
        if (updatedSection.fields && updatedSection.fields.length > 0) {
            changedSection.fields = updatedSection.fields.map((updatedField) => {
                const currentFieldData = currentFieldsMap.get(updatedField.id);

                // Create a copy of the updated field with all properties preserved
                const changedField: DisplayField = {
                    ...updatedField,
                };

                changedField.value = diffValues(currentFieldData?.field.value, updatedField.value);

                // Show the name as inserted if it's new or different
                if (!currentFieldData || !currentFieldData.field || currentFieldData.field.name !== updatedField.name) {
                    changedField.name = `<ins>${updatedField.name}</ins>`;
                }

                return changedField;
            });
        } else {
            // Keep fields from updated section (could be null or empty array)
            changedSection.fields = updatedSection.fields;
        }

        return changedSection;
    });

    const pendingDoc: PendingDisplayDocumentChanges = {
        content: mergedSections,
        changes: changes,
        accepted: false,
    };

    return pendingDoc;
};

/**
 * Call the LLM service with document context
 * @param context - Document context and user input
 * @returns Promise with LLM response
 */
interface ScoutStreamLine {
    type: "progress" | "result" | "error";
    message?: string;
    data?: { scoutSummary: string; intent: string; updatedSections?: DisplaySection[] };
}

/**
 * Splits complete NDJSON lines out of a growing buffer, returning the leftover
 * partial line (if a chunk boundary split a line) as the new buffer to prepend
 * to the next chunk.
 */
export function parseScoutStreamBuffer(buffer: string): { lines: ScoutStreamLine[]; remainder: string } {
    const lines: ScoutStreamLine[] = [];
    let remainder = buffer;
    let newlineIndex = remainder.indexOf("\n");
    while (newlineIndex !== -1) {
        const rawLine = remainder.slice(0, newlineIndex).trim();
        remainder = remainder.slice(newlineIndex + 1);
        if (rawLine) {
            lines.push(JSON.parse(rawLine));
        }
        newlineIndex = remainder.indexOf("\n");
    }
    return { lines, remainder };
}

/**
 * Calls the Scout chat endpoint's streamed NDJSON response directly (bypassing apiFetch,
 * which doesn't support reading a streaming body), invoking onProgress for each status
 * line and resolving with the final result once it arrives.
 */
async function callScoutChatStreaming(
    body: Record<string, unknown>,
    onProgress?: (message: string) => void,
): Promise<{ scoutSummary: string; intent: string; updatedSections?: DisplaySection[] }> {
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        throw new Error("No effective session found");
    }
    const { userId, accessToken, isImpersonating } = effectiveSession;
    const selectedClinicAccountId = getStoredSelectedClinicAccountId(userId);

    const response = await fetch(`${VITE_HAPPYDOC_API_SERVER_URL}/scout`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
            ...(isImpersonating ? { "X-Effective-User-Id": userId } : {}),
            ...(selectedClinicAccountId ? { "X-HappyDoc-Account-Id": selectedClinicAccountId } : {}),
        },
        body: JSON.stringify(body),
    });

    if (!response.ok || !response.body) {
        throw new Error(`Scout API returned ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let result: { scoutSummary: string; intent: string; updatedSections?: DisplaySection[] } | undefined;

    for (;;) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseScoutStreamBuffer(buffer);
        buffer = parsed.remainder;
        for (const line of parsed.lines) {
            if (line.type === "progress" && line.message) {
                onProgress?.(line.message);
            } else if (line.type === "result" && line.data) {
                result = line.data;
            } else if (line.type === "error") {
                throw new Error(line.message || "Scout API returned an error");
            }
        }
    }

    if (!result) {
        throw new Error("Scout API stream ended without a result");
    }
    return result;
}

const determineScoutOperation = async (context: DocumentContext): Promise<ScoutOperation> => {
    try {
        const body = {
            userInstructions: context.userInput,
            documentTypeId: context.document?.document_type_id,
            documentContent: context.documentContent,
            documentTypeDefinition: context.documentTypeDefinition,
            documentId: context.document?.id,
            patientRecordId: context.document?.patient_record_id,
            ...(context.conversationHistory?.length ? { conversationHistory: context.conversationHistory } : {}),
        };

        const data = body.documentTypeId
            ? await apiFetch<{
                  scoutSummary: string;
                  intent: string;
                  updatedSections?: DisplaySection[];
              }>("scout", { method: "POST", json: body })
            : await callScoutChatStreaming(body, context.onProgress);

        if (!data) {
            throw new Error("Scout API returned no data");
        }

        return {
            scoutSummary: data.scoutSummary,
            intent: data.intent as ScoutIntent,
            updatedSections: (data.updatedSections as DisplaySection[]) || [],
        };
    } catch (error) {
        console.error("Error calling scout API:", error);
        throw error;
    }
};
